/**
 * ScoresSection Component
 * Score weight configuration with interconnected sliders.
 * Moving one slider auto-redistributes others proportionally.
 * Stacked bar + component cards with lock toggle.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Lock, Unlock, HelpCircle, BarChart3 } from 'lucide-react';
import { SimulateImpactModal } from '../SimulateImpactModal';
import { simulateConfigImpact } from '@/services/credit-engine.service';
import { useToast } from '@/hooks';
import { PARTNER_ID } from '@/constants';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ScoreItem {
  pk?: string;
  sk?: string;
  score_name: string;
  weight: number;
  max_points: number;
  display_order?: number;
  is_active?: boolean;
  description?: string;
  rules?: any[];
}

interface ScoresSectionProps {
  data: ScoreItem[] | null;
  canEdit: boolean;
  onSave: (data: any) => void;
  isSaving: boolean;
}

// ─── Theme config per component ───────────────────────────────────────────────

interface ScoreTheme {
  label: string;
  color: string;
  description: string;
  helpTitle: string;
  helpBody: string;
}

const SCORE_THEMES: Record<string, ScoreTheme> = {
  income_stability: {
    label: 'Estabilidad de Ingresos',
    color: '#10B981',
    description: 'Regularidad y frecuencia de depositos.',
    helpTitle: 'Estabilidad de Ingresos',
    helpBody: 'Mide que tan constante es el patron de depositos del cliente. Evalua la frecuencia detectada (semanal, quincenal, mensual), la desviacion estandar de montos, y la cantidad de fuentes de ingreso. Un patron regular y predecible obtiene mayor puntaje.',
  },
  payment_capacity: {
    label: 'Capacidad de Pago',
    color: '#3B82F6',
    description: 'Ingreso disponible vs cuota semanal.',
    helpTitle: 'Capacidad de Pago',
    helpBody: 'Calcula el ratio entre ingreso disponible mensual y la cuota semanal proyectada. Considera el ingreso neto despues de gastos estimados. Un ratio alto indica que el cliente puede cubrir la cuota con holgura, reduciendo riesgo de mora.',
  },
  mexico_transfers: {
    label: 'Transferencias a Mexico',
    color: '#F59E0B',
    description: 'Volumen y frecuencia de envios a MX.',
    helpTitle: 'Transferencias a Mexico',
    helpBody: 'Evalua el historial de transferencias del cliente hacia Mexico en los ultimos 90 dias. Mayor volumen y frecuencia sugieren vinculo economico activo con Mexico, lo que correlaciona con mejor comportamiento de pago en creditos MXN.',
  },
  seniority: {
    label: 'Antiguedad',
    color: '#8B5CF6',
    description: 'Tiempo como cliente activo.',
    helpTitle: 'Antiguedad',
    helpBody: 'Mide el tiempo transcurrido desde que el cliente abrio su cuenta. Mayor antiguedad demuestra permanencia y estabilidad, factores positivos para el scoring. El puntaje escala con el tiempo hasta un tope definido en las reglas.',
  },
  income_level: {
    label: 'Nivel de Ingresos',
    color: '#EC4899',
    description: 'Monto absoluto de ingresos mensuales.',
    helpTitle: 'Nivel de Ingresos',
    helpBody: 'Evalua el monto total de ingresos mensuales del cliente. No mide estabilidad (eso lo hace income_stability), sino el nivel absoluto de los montos. Un ingreso alto permite mayores cuotas semanales y reduce el riesgo relativo del credito.',
  },
};

const DEFAULT_THEME: ScoreTheme = {
  label: '?', color: '#666', description: '',
  helpTitle: 'Componente', helpBody: 'Sin descripcion disponible.',
};

const MIN_WEIGHT = 0.01; // 1% minimum per component

// ─── Redistribution logic ─────────────────────────────────────────────────────

function redistributeWeights(
  items: ScoreItem[],
  changedIndex: number,
  newWeight: number,
  locked: Set<number>,
): ScoreItem[] {
  const updated = items.map((item) => ({ ...item }));

  // Find unlocked indices (excluding the changed one)
  const unlocked = updated
    .map((_, i) => i)
    .filter((i) => i !== changedIndex && !locked.has(i));

  if (unlocked.length === 0) return items; // can't redistribute — no change

  // Total weight of unlocked others
  const totalUnlocked = unlocked.reduce((s, i) => s + updated[i]!.weight, 0);
  const lockedTotal = updated.reduce((s, item, i) =>
    i !== changedIndex && locked.has(i) ? s + item.weight : s, 0);

  // Clamp: can't take more than available from unlocked (leaving MIN_WEIGHT each)
  const maxTakeable = totalUnlocked - unlocked.length * MIN_WEIGHT;
  const clampedNew = Math.max(
    MIN_WEIGHT,
    Math.min(updated[changedIndex]!.weight + maxTakeable, newWeight),
  );

  if (Math.abs(clampedNew - updated[changedIndex]!.weight) < 0.0001) return updated;

  updated[changedIndex]!.weight = clampedNew;

  // Target total for all unlocked items
  const newUnlockedTotal = 1.0 - clampedNew - lockedTotal;

  if (totalUnlocked > 0.001) {
    // Scale proportionally — all unlocked grow/shrink by same factor
    const scale = newUnlockedTotal / totalUnlocked;
    for (const i of unlocked) {
      updated[i]!.weight = Math.max(MIN_WEIGHT, updated[i]!.weight * scale);
    }
  } else {
    // All at zero/near-zero: distribute equally
    const each = newUnlockedTotal / unlocked.length;
    for (const i of unlocked) {
      updated[i]!.weight = Math.max(MIN_WEIGHT, each);
    }
  }

  // Fix floating-point drift: adjust last unlocked item so sum is exactly 1.0
  const sumNow = updated.reduce((s, item) => s + item.weight, 0);
  const drift = sumNow - 1.0;
  if (Math.abs(drift) > 0.0001 && unlocked.length > 0) {
    const last = unlocked[unlocked.length - 1]!;
    updated[last]!.weight = Math.max(MIN_WEIGHT, updated[last]!.weight - drift);
  }

  return updated;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ScoresSection({ data, canEdit, onSave, isSaving }: ScoresSectionProps) {
  const toast = useToast();
  const [draft, setDraft] = useState<ScoreItem[] | null>(null);
  const [locked, setLocked] = useState<Set<number>>(new Set());
  const [helpIndex, setHelpIndex] = useState<number | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationData, setSimulationData] = useState<any>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const sorted = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [data]);

  const current = useMemo(() => draft ?? sorted, [draft, sorted]);
  const isDirty = draft !== null;

  // ─── Sensitivity: avg_score delta per +1% on each component ─────────
  // Computed once on mount from saved config. Approximately constant
  // for linear scoring (depends on avg component scores, not weights).
  const [sensitivity, setSensitivity] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    if (sorted.length === 0 || sensitivity) return;
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.allSettled(
          sorted.map((_, idx) => {
            const tweaked = redistributeWeights(
              structuredClone(sorted),
              idx,
              sorted[idx]!.weight + 0.01,
              new Set<number>(),
            );
            return simulateConfigImpact(PARTNER_ID, 'scores', tweaked);
          }),
        );
        if (cancelled) return;
        const sens: Record<string, number> = {};
        results.forEach((r, idx) => {
          if (r.status === 'fulfilled') {
            sens[sorted[idx]!.score_name] = r.value?.data?.delta?.avg_score ?? 0;
          }
        });
        setSensitivity(sens);
      } catch {
        // silently fail — sensitivity is informational only
      }
    })();
    return () => { cancelled = true; };
  }, [sorted, sensitivity]);

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Sin datos de scoring — el endpoint GET /config/scores devuelve error.
        Verificar que el backend este desplegado con soporte para configType=scores.
      </p>
    );
  }

  function ensureDraft(): ScoreItem[] {
    if (draft) return draft;
    const d = structuredClone(sorted);
    setDraft(d);
    return d;
  }

  function toggleLock(index: number) {
    setLocked((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  // ─── Slider change from card ────────────────────────────────────────────

  function handleSliderChange(index: number, newPct: number) {
    const newWeight = Math.max(MIN_WEIGHT, Math.min(1, newPct / 100));
    setDraft((prev) => {
      const base = prev ?? structuredClone(sorted);
      return redistributeWeights(base, index, newWeight, locked);
    });
  }

  // ─── Stacked bar drag ──────────────────────────────────────────────────

  function startBarDrag(dividerIndex: number, e: React.MouseEvent | React.TouchEvent) {
    if (!canEdit) return;
    e.preventDefault();
    const bar = barRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const base = ensureDraft();

    // Cumulative weight up to divider (left side total)
    const cumulativeBefore = base.slice(0, dividerIndex + 1).reduce((s, item) => s + item.weight, 0);

    const onMove = (clientX: number) => {
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      // The divider is between items[dividerIndex] and items[dividerIndex+1]
      // New cumulative weight at this divider position = pct
      const leftItem = base[dividerIndex]!;
      const rightItem = base[dividerIndex + 1]!;
      const pairTotal = leftItem.weight + rightItem.weight;
      const cumulativeLeft = cumulativeBefore - leftItem.weight; // weight of everything before dividerIndex

      const newLeftWeight = Math.max(MIN_WEIGHT, Math.min(pairTotal - MIN_WEIGHT, pct - cumulativeLeft));
      const newRightWeight = pairTotal - newLeftWeight;

      const updated = base.map((item) => ({ ...item }));
      updated[dividerIndex]!.weight = newLeftWeight;
      updated[dividerIndex + 1]!.weight = newRightWeight;

      setDraft(updated);
    };

    if ('touches' in e) {
      const move = (ev: TouchEvent) => { ev.preventDefault(); if (ev.touches[0]) onMove(ev.touches[0].clientX); };
      const end = () => { document.removeEventListener('touchmove', move); document.removeEventListener('touchend', end); };
      document.addEventListener('touchmove', move, { passive: false });
      document.addEventListener('touchend', end);
    } else {
      const move = (ev: MouseEvent) => onMove(ev.clientX);
      const end = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', end); };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', end);
    }
  }

  // ─── Simulate & save ──────────────────────────────────────────────────

  async function handleSimulate() {
    if (!draft) return;
    setIsSimulating(true);
    try {
      const res = await simulateConfigImpact(PARTNER_ID, 'scores', draft);
      const impact = res.data;
      if (impact?.error === 'NO_PROFILES') {
        toast.error('No hay perfiles financieros para simular.');
        return;
      }
      setSimulationData(impact);
    } catch (err: any) {
      console.error('Simulate impact error:', err?.response?.data ?? err);
      toast.error('Error al simular impacto: ' + (err?.response?.data?.msg || err?.message || 'Error desconocido'));
    } finally {
      setIsSimulating(false);
    }
  }

  function handleApplyAfterSimulation() {
    if (draft) onSave(draft);
    setSimulationData(null);
    setDraft(null);
    setLocked(new Set());
  }

  // ─── Help modal content ────────────────────────────────────────────────

  const helpItem = helpIndex !== null ? current[helpIndex] : null;
  const helpTheme = helpItem ? (SCORE_THEMES[helpItem.score_name] ?? DEFAULT_THEME) : null;

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Header */}
      <p className="text-xs text-muted-foreground">
        Pesos de cada componente del score crediticio. {canEdit ? 'Arrastra los divisores o mueve los sliders para ajustar.' : 'La suma siempre es 100%.'}
      </p>

      {/* Stacked bar */}
      <div className="rounded-lg border bg-card p-3">
        <div className="mb-1.5 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>0%</span>
          <span>{canEdit ? 'Arrastra los divisores para redistribuir pesos' : 'Distribucion de pesos'}</span>
          <span>100%</span>
        </div>
        <div ref={barRef} className="relative h-12 w-full rounded-md overflow-hidden select-none flex">
          {current.map((item, i) => {
            const theme = SCORE_THEMES[item.score_name] ?? DEFAULT_THEME;
            const pct = item.weight * 100;
            return (
              <div
                key={item.score_name}
                className="relative h-full flex flex-col items-center justify-center overflow-hidden transition-all duration-150"
                style={{
                  width: `${pct}%`,
                  backgroundColor: `${theme.color}25`,
                  borderRight: i < current.length - 1 ? `2px solid ${theme.color}60` : undefined,
                }}
              >
                {pct >= 8 && (
                  <>
                    <span className="text-[10px] font-bold truncate px-0.5 leading-tight" style={{ color: theme.color }}>
                      {theme.label.split(' ')[0]}
                    </span>
                    <span className="text-[11px] font-bold tabular-nums" style={{ color: theme.color }}>
                      {canEdit ? pct.toFixed(1) : pct.toFixed(0)}%
                    </span>
                  </>
                )}
              </div>
            );
          })}

          {/* Draggable dividers */}
          {canEdit && current.map((_, i) => {
            if (i >= current.length - 1) return null;
            // Position: cumulative weight up to and including item i
            const leftPct = current.slice(0, i + 1).reduce((s, it) => s + it.weight * 100, 0);
            return (
              <div
                key={`divider-${i}`}
                className="absolute top-0 z-10 flex h-full items-center justify-center"
                style={{ left: `${leftPct}%`, transform: 'translateX(-50%)', width: '20px', cursor: 'col-resize' }}
                onMouseDown={(e) => startBarDrag(i, e)}
                onTouchStart={(e) => startBarDrag(i, e)}
              >
                <div className="h-10 w-1.5 rounded-full bg-foreground/80 shadow-md transition-transform hover:scale-x-150" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Component cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {current.map((item, index) => {
          const theme = SCORE_THEMES[item.score_name] ?? DEFAULT_THEME;
          const pctExact = item.weight * 100;
          const pctSlider = Math.round(pctExact);
          const pctLabel = canEdit ? pctExact.toFixed(1) : pctSlider.toString();
          const isLocked = locked.has(index);
          const isActive = item.is_active !== false;

          return (
            <div
              key={item.score_name}
              className={`rounded-lg border bg-card p-3 border-l-[3px] ${!isActive ? 'opacity-40' : ''}`}
              style={{ borderLeftColor: theme.color }}
            >
              {/* Title row */}
              <div className="mb-1 flex items-center justify-between gap-1">
                <span className="text-xs font-bold truncate" style={{ color: theme.color }}>
                  {theme.label}
                </span>
                <button
                  type="button"
                  onClick={() => setHelpIndex(index)}
                  className="shrink-0 text-muted-foreground transition-colors hover:text-sky-400"
                  aria-label={`Info ${theme.label}`}
                >
                  <HelpCircle size={13} />
                </button>
              </div>

              {/* Description */}
              <p className="mb-2 text-[10px] leading-snug text-muted-foreground">
                {theme.description}
              </p>

              {/* Sensitivity indicator */}
              {(() => {
                const sens = sensitivity?.[item.score_name];
                if (sens == null) return null;
                const isPositive = sens > 0;
                return (
                  <div
                    className="mb-2 flex items-center gap-1.5 rounded-md px-2 py-1"
                    style={{ backgroundColor: `${theme.color}12` }}
                    title="Cambio en score promedio al subir este peso 1%"
                  >
                    <span
                      className="text-[10px] font-bold"
                      style={{ color: isPositive ? '#16a34a' : sens < 0 ? '#dc2626' : '#64748b' }}
                    >
                      {isPositive ? '▲' : sens < 0 ? '▼' : '—'}
                    </span>
                    <span className="text-[10px] font-bold tabular-nums" style={{ color: theme.color }}>
                      {sens > 0 ? '+' : ''}{sens.toFixed(2)} pts
                    </span>
                    <span className="text-[9px] text-muted-foreground">/ 1%</span>
                  </div>
                );
              })()}

              {/* Weight display + slider */}
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-lg font-bold tabular-nums" style={{ color: theme.color }}>
                    {pctLabel}%
                  </span>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => toggleLock(index)}
                      className={`rounded p-0.5 transition-colors ${isLocked ? 'text-amber-500' : 'text-muted-foreground hover:text-foreground'}`}
                      aria-label={isLocked ? 'Desbloquear' : 'Bloquear'}
                      title={isLocked ? 'Bloqueado — no se redistribuye' : 'Click para bloquear este peso'}
                    >
                      {isLocked ? <Lock size={13} /> : <Unlock size={13} />}
                    </button>
                  )}
                </div>
                {canEdit ? (
                  <input
                    type="range"
                    min={1}
                    max={99}
                    step={1}
                    value={pctSlider}
                    onChange={(e) => handleSliderChange(index, Number(e.target.value))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, ${theme.color} 0%, ${theme.color} ${pctExact}%, var(--color-muted, #e5e7eb) ${pctExact}%, var(--color-muted, #e5e7eb) 100%)`,
                      accentColor: theme.color,
                    }}
                  />
                ) : (
                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${pctSlider}%`, backgroundColor: theme.color }}
                    />
                  </div>
                )}
              </div>

              {/* Meta: max points, active, rules */}
              <div className="space-y-0.5 text-[10px]">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Max pts</span>
                  <span className="font-semibold tabular-nums text-foreground">{item.max_points}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Activo</span>
                  {isActive
                    ? <span className="font-medium text-green-600">Si</span>
                    : <span className="font-medium text-red-600">No</span>
                  }
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reglas</span>
                  <span className="font-semibold tabular-nums text-foreground">{item.rules?.length ?? 0}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Sticky save bar */}
      {isDirty && (
        <div className="sticky bottom-4 flex items-center justify-end gap-2 rounded-lg border bg-card p-2.5 shadow-lg">
          <span className="mr-auto text-xs text-muted-foreground">Cambios sin guardar</span>
          <Button variant="outline" size="sm" onClick={() => { setDraft(null); setLocked(new Set()); }}>
            Descartar
          </Button>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<BarChart3 size={14} />}
            onClick={handleSimulate}
            isLoading={isSimulating}
          >
            {isSimulating ? 'Simulando...' : 'Simular y Guardar'}
          </Button>
        </div>
      )}

      {/* Help modal */}
      <Modal
        isOpen={helpIndex !== null}
        onClose={() => setHelpIndex(null)}
        title={helpTheme?.helpTitle ?? ''}
        size="sm"
      >
        {helpTheme && helpItem && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center rounded px-2 py-0.5 text-xs font-bold"
                style={{ color: helpTheme.color, backgroundColor: `${helpTheme.color}15` }}
              >
                {helpTheme.label}
              </span>
              <span className="text-xs text-muted-foreground font-mono">{helpItem.score_name}</span>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed">
              {helpTheme.helpBody}
            </p>

            <div className="rounded-lg border px-4 py-3" style={{ borderColor: `${helpTheme.color}30`, backgroundColor: `${helpTheme.color}08` }}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Peso actual</span>
                <span className="font-bold tabular-nums" style={{ color: helpTheme.color }}>
                  {(helpItem.weight * 100).toFixed(0)}%
                </span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-muted-foreground">Puntos maximos</span>
                <span className="font-bold tabular-nums" style={{ color: helpTheme.color }}>
                  {helpItem.max_points}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-muted-foreground">Reglas configuradas</span>
                <span className="font-bold tabular-nums" style={{ color: helpTheme.color }}>
                  {helpItem.rules?.length ?? 0}
                </span>
              </div>
            </div>

            <div className="rounded-lg bg-muted/50 px-4 py-2.5">
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">Formula:</strong> score_parcial = (puntos_obtenidos / {helpItem.max_points}) × {(helpItem.weight * 100).toFixed(0)} pts del score total
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Simulation Modal */}
      {simulationData && draft && (
        <SimulateImpactModal
          data={simulationData}
          configType="scores"
          configChanges={sorted.map((item, i) => ({
            key: item.score_name,
            label: (SCORE_THEMES[item.score_name] ?? DEFAULT_THEME).label,
            color: (SCORE_THEMES[item.score_name] ?? DEFAULT_THEME).color,
            before: item.weight * 100,
            after: draft[i]!.weight * 100,
            unit: '%',
          }))}
          onApply={handleApplyAfterSimulation}
          onCancel={() => setSimulationData(null)}
          isApplying={isSaving}
        />
      )}
    </div>
  );
}
